import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { folderApi } from "../services/api";
import toast from "react-hot-toast";
import { moveFilesToFolder, moveFoldersToFolder } from "../utils/moveItems";

const MAX_VISIBLE_CRUMBS = 3; // Show first + ... + last 2

const FolderBreadcrumbs = ({
  folderId,
  collectionName,
  collectionDisplayName,
  onNavigate,
  onRefresh,
  translate,
  isDraggingItem = false,
  onUploadToFolder,
  setMovingFilePaths,
  setMovingFolderIds,
  onOptimisticRemoveFiles,
  onOptimisticRemoveFolders,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [movingToId, setMovingToId] = useState(null);
  const [showCollapsed, setShowCollapsed] = useState(false);
  const collapsedMenuRef = useRef(null);

  useEffect(() => {
    if (!folderId) {
      setBreadcrumbs([]);
      setLoading(false);
      return;
    }

    const existingIndex = breadcrumbs.findIndex((b) => b.id === folderId);
    if (existingIndex !== -1) {
      setBreadcrumbs(breadcrumbs.slice(0, existingIndex + 1));
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    folderApi.getFolderBreadcrumbs(folderId).then((result) => {
      if (!cancelled) setBreadcrumbs(result.breadcrumbs || []);
    }).catch(() => {
      if (!cancelled) setBreadcrumbs([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close collapsed menu on click outside
  useEffect(() => {
    if (!showCollapsed) return;
    const handleClick = (e) => {
      if (collapsedMenuRef.current && !collapsedMenuRef.current.contains(e.target)) {
        setShowCollapsed(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showCollapsed]);

  if (!folderId) return null;

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();
    setDragOverId(null);

    // Check if this is an OS file drop (upload to folder)
    const osFiles = Array.from(e.dataTransfer.files);
    if (osFiles.length > 0 && !e.dataTransfer.getData("application/json")) {
      e.stopPropagation(); // Prevent container's onDrop from also handling this
      if (onUploadToFolder) onUploadToFolder(osFiles, targetFolderId, e.dataTransfer.items);
      return;
    }

    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      const crumbKey = targetFolderId || "root";
      setMovingToId(crumbKey);
      if (data.type === "files" || data.type === "file") {
        const paths = data.type === "files" ? data.ids : [data.id];
        await moveFilesToFolder(paths, targetFolderId, {
          collectionName,
          translate,
          setMovingFilePaths,
          onOptimisticRemoveFiles,
        });
      } else if (data.type === "folders" || data.type === "folder") {
        const ids = (data.type === "folders" ? data.ids : [data.id])
          .filter((id) => id !== targetFolderId);
        await moveFoldersToFolder(ids, targetFolderId, {
          translate,
          setMovingFolderIds,
          onOptimisticRemoveFolders,
        });
      }
    } catch (err) {
      if (setMovingFilePaths) setMovingFilePaths(new Set());
      if (setMovingFolderIds) setMovingFolderIds(new Set());
      toast.error(err.response?.data?.detail || translate("failedToMove"));
    } finally {
      setMovingToId(null);
    }
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    setDragOverId(id);
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const crumbStyle = (id, isLast) => {
    const isDragTarget = dragOverId === id;
    const isMoving = movingToId === id;
    return {
      color: isLast && !isDragTarget ? "var(--color-text-primary)" : "var(--color-mint)",
      backgroundColor: isDragTarget ? "var(--color-mint-muted)" : "transparent",
      borderBottom: isDragTarget ? "2px solid var(--color-mint)" : "2px solid transparent",
      opacity: isMoving ? 0.5 : 1,
      transition: "all 0.15s",
    };
  };

  const dropLabel = (id) => {
    if (!isDraggingItem || movingToId) return null;
    return (
      <span
        className="text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap transition-all"
        style={{
          backgroundColor: dragOverId === id ? "var(--color-mint)" : "var(--color-surface-raised)",
          color: dragOverId === id ? "#fff" : "var(--color-text-muted)",
          border: dragOverId === id ? "1px solid var(--color-mint)" : "1px solid var(--color-border)",
        }}
      >
        {translate ? translate("dropHereToMove") : "Drop here"}
      </span>
    );
  };

  const spinner = (id) => {
    if (movingToId !== id) return null;
    return (
      <span className="inline-block" style={{ width: 14, height: 14, border: "2px solid var(--color-mint)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    );
  };

  const renderCrumb = (crumb, isLast) => {
    const Tag = isLast ? "span" : "button";
    return (
      <React.Fragment key={crumb.id}>
        <ChevronRight size={14} style={{ color: "var(--color-text-muted)", opacity: 0.5 }} className="flex-shrink-0" />
        <Tag
          {...(!isLast && { onClick: () => onNavigate(crumb.id) })}
          className={`${!isLast ? "hover:underline cursor-pointer font-medium text-base" : "font-semibold text-lg"} truncate rounded px-1 -mx-1 pb-0.5`}
          style={{ ...crumbStyle(crumb.id, isLast), maxWidth: "300px" }}
          title={crumb.name}
          {...(!isLast && {
            onDragOver: (e) => handleDragOver(e, crumb.id),
            onDragLeave: handleDragLeave,
            onDrop: (e) => handleDrop(e, crumb.id),
          })}
        >
          {crumb.name}
        </Tag>
        {!isLast && dropLabel(crumb.id)}
        {!isLast && spinner(crumb.id)}
      </React.Fragment>
    );
  };

  // Determine which crumbs to show vs collapse
  const needsCollapse = breadcrumbs.length > MAX_VISIBLE_CRUMBS;
  const collapsedCrumbs = needsCollapse ? breadcrumbs.slice(1, breadcrumbs.length - 2) : [];
  const visibleStart = needsCollapse ? [breadcrumbs[0]] : [];
  const visibleEnd = needsCollapse ? breadcrumbs.slice(breadcrumbs.length - 2) : breadcrumbs;

  return (
    <div className="flex items-center gap-1.5 text-sm">
      {/* Collection root */}
      <button
        onClick={() => onNavigate(null)}
        className="hover:underline font-medium text-base rounded px-1 -mx-1 pb-0.5 flex-shrink-0"
        style={crumbStyle("root", false)}
        onDragOver={(e) => handleDragOver(e, "root")}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, null)}
      >
        {collectionDisplayName}
      </button>
      {dropLabel("root")}
      {spinner("root")}

      {/* First crumb (if collapsed) */}
      {needsCollapse && visibleStart.map((crumb) => renderCrumb(crumb, false))}

      {/* Collapsed crumbs dropdown */}
      {needsCollapse && (
        <>
          <ChevronRight size={14} style={{ color: "var(--color-text-muted)", opacity: 0.5 }} className="flex-shrink-0" />
          <div className="relative" ref={collapsedMenuRef}>
            <button
              onClick={() => setShowCollapsed(!showCollapsed)}
              className="font-semibold text-lg rounded px-1.5 -mx-1 pb-0.5 hover:bg-[var(--color-surface-raised)] transition-colors flex items-center gap-0.5"
              style={{ color: "var(--color-text-muted)" }}
            >
              ...
              <ChevronDown size={12} style={{ transform: showCollapsed ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
            </button>
            {showCollapsed && (
              <div
                className="absolute left-0 top-full mt-1 w-56 rounded-xl shadow-lg z-30 py-1 max-h-64 overflow-y-auto"
                style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}
              >
                {collapsedCrumbs.map((crumb) => (
                  <button
                    key={crumb.id}
                    onClick={() => { onNavigate(crumb.id); setShowCollapsed(false); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] transition-colors truncate"
                    style={{ color: "var(--color-text-secondary)" }}
                    title={crumb.name}
                    onDragOver={(e) => handleDragOver(e, crumb.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => { handleDrop(e, crumb.id); setShowCollapsed(false); }}
                  >
                    {crumb.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Visible end crumbs (or all if not collapsed) */}
      {visibleEnd.map((crumb, index) => {
        const isLast = !loading && (needsCollapse
          ? index === visibleEnd.length - 1
          : index === breadcrumbs.length - 1);
        return renderCrumb(crumb, isLast);
      })}

      {/* Skeleton for loading */}
      {loading && (
        <>
          <ChevronRight size={14} style={{ color: "var(--color-text-muted)", opacity: 0.5 }} className="flex-shrink-0" />
          <div className="h-5 w-28 rounded bg-[var(--color-skeleton-light)] animate-pulse" />
        </>
      )}
    </div>
  );
};

export default FolderBreadcrumbs;
